import type { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import {
    ActivitySeries,
    HistoryCountsBucket,
} from "@common/schemas/domains/activity/activity-timeline.schema";
import type {
    HistoryCountsSeries,
    HistoryDailyCount,
    HistoryFilter,
} from "@common/types/auditLog";
import type {
    DensityChartDay,
    DensityChartSeries,
    DensityChartWindow,
} from "@design-system/DensityChart/DensityChart.lib";
import type { DensityChartBucketUnit } from "@design-system/DensityChart/densityBuckets.lib";
import {
    toLocalDayEnd,
    toLocalDayStart,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";

/** What a chart series counts: a series, or with `personId` that person's part of People. */
export interface TimelineSeriesKey {
    series: ActivitySeries;
    personId?: number;
}

export interface ActivityTimelineSeries
    extends DensityChartSeries, TimelineSeriesKey {}

/** Stack order, bottom first, colored by the `--chart-series-*` tokens. */
export const ACTIVITY_TIMELINE_SERIES: readonly ActivityTimelineSeries[] = [
    {
        id: ActivitySeries.PEOPLE,
        series: ActivitySeries.PEOPLE,
        label: "People",
        color: "var(--chart-series-people)",
        accentColor: "var(--chart-series-people-accent)",
        selectedColor: "var(--chart-series-people-selected)",
        hoverColor: "var(--chart-series-people-hover)",
    },
    {
        id: ActivitySeries.KICK,
        series: ActivitySeries.KICK,
        label: "Kick",
        color: "var(--chart-series-kick)",
        accentColor: "var(--chart-series-kick-accent)",
        selectedColor: "var(--chart-series-kick-selected)",
        hoverColor: "var(--chart-series-kick-hover)",
    },
    {
        id: ActivitySeries.INTEGRATIONS,
        series: ActivitySeries.INTEGRATIONS,
        label: "Integrations",
        color: "var(--chart-series-integrations)",
        accentColor: "var(--chart-series-integrations-accent)",
        selectedColor: "var(--chart-series-integrations-selected)",
        hoverColor: "var(--chart-series-integrations-hover)",
    },
    {
        id: ActivitySeries.RULES,
        series: ActivitySeries.RULES,
        label: "Rules",
        color: "var(--chart-series-rules)",
        accentColor: "var(--chart-series-rules-accent)",
        selectedColor: "var(--chart-series-rules-selected)",
        hoverColor: "var(--chart-series-rules-hover)",
    },
];

/** The counts a chart series draws from one row; none when it changed nothing. */
export const toSeriesCounts = (
    row: HistoryDailyCount,
    { series, personId }: TimelineSeriesKey,
): HistoryCountsSeries | undefined =>
    personId === undefined
        ? row.series?.[series]
        : row.people?.[String(personId)];

const BUCKETS_BY_UNIT: Record<DensityChartBucketUnit, HistoryCountsBucket> = {
    day: HistoryCountsBucket.DAY,
    week: HistoryCountsBucket.WEEK,
    month: HistoryCountsBucket.MONTH,
};

/** Counts in the bars' size, so a bar counts an object once. */
export const toTimelineBucket = (
    unit: DensityChartBucketUnit,
): HistoryCountsBucket => BUCKETS_BY_UNIT[unit];

/**
 * Count rows as chart days. Values are each chart series' distinct objects
 * (`series[x].total`, or a person's), not the row's `count`: one object can
 * be in several series.
 */
export function toTimelineDays(
    rows: readonly HistoryDailyCount[],
    timelineSeries: readonly ActivityTimelineSeries[],
): DensityChartDay[] {
    return rows.map((row) => ({
        date: row.date,
        values: Object.fromEntries(
            timelineSeries.map((chartSeries) => [
                chartSeries.id,
                toSeriesCounts(row, chartSeries)?.total ?? 0,
            ]),
        ),
    }));
}

/** The series with at least one object in `days`; empty ones aren't drawn. */
export const visibleTimelineSeries = (
    days: readonly DensityChartDay[],
    timelineSeries: readonly ActivityTimelineSeries[],
) =>
    timelineSeries.filter(({ id }) =>
        days.some((day) => (day.values[id] ?? 0) > 0),
    );

/**
 * Instants covering whole local days, from local midnight on `start` to just
 * before local midnight after `end`, matching how counts bucket days.
 */
export const toCreatedAtBounds = ({ start, end }: DensityChartWindow) => ({
    minCreatedAt: toLocalDayStart(start),
    maxCreatedAt: toLocalDayEnd(end),
});

/** A History list's filter with the timeline on: the window's days, the outcome and the series picks. */
export const toTimelineListFilter = <Filter extends HistoryFilter>({
    filter,
    outcome,
    series,
    window,
}: {
    filter: Filter;
    outcome: ActivityOutcomeKind | undefined;
    series: readonly ActivitySeries[] | undefined;
    window: DensityChartWindow;
}): Filter => ({
    ...filter,
    ...toCreatedAtBounds(window),
    ...(outcome && { outcome }),
    ...(series && series.length > 0 && { series: [...series] }),
});
